import { useState } from 'react';
import { BsHeart, BsHeartFill } from 'react-icons/bs';
import { FaBell } from 'react-icons/fa';
import { FaLock } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import GetNotifiedModal from '../modal/haggle-event/GetNotifiedModal';
import { formatDateTimeShort, formatPrice } from '../utils/utils';

import { useModal } from '@/hooks/useModal';
import { useUser } from '@/hooks/useUser';
import { IHaggleEvent } from '@/interfaces/api/IHaggleEvent';
import { ProductCardType } from '@/interfaces/notifications/Notifications';
import { haggleApiService } from '@/services/haggle.service';

type ProductCardProps = {
	productImageAlt: string;
	productImageSrc: string;
	productName: string;
	productVendor: string;
	productTradeName: string;
	productPrice: string;
	productHref: string;
	cardType?: ProductCardType;
	notificationRequiredInfo?: {
		haggleStartDate?: string;
		haggleEventId?: number;
		productId: number;
		productName: string;
		productUrl: string;
		sent: false;
	};
	productHaggle?: IHaggleEvent | undefined;
	isFavourite?: boolean;
	refetchFavourites?: () => void;
};

const ProductCard = ({
	productImageAlt,
	productImageSrc,
	productName,
	productVendor,
	productTradeName,
	productPrice,
	productHref,
	cardType = ProductCardType.Store,
	notificationRequiredInfo,
	productHaggle,
	isFavourite,
	refetchFavourites,
}: ProductCardProps) => {
	const detailsPageUrl = (isCSVHaggle: boolean) => {
		return (
			'/' +
			productTradeName +
			`${isCSVHaggle ? '/product-detail/' : '/product-view/'}` +
			notificationRequiredInfo?.productId
		);
	};

	const getProductUrl = (url: string) => {
		if (url && url !== '') {
			return url;
		}
		return (
			import.meta.env.VITE_FRONTEND_URL +
			detailsPageUrl(productHaggle?.isCSVHaggle || false)
		);
	};
	const { modal, openModal, closeModal } = useModal({
		children: (
			<GetNotifiedModal
				notificationRequiredInfo={
					notificationRequiredInfo
						? {
								...notificationRequiredInfo,
								productImage: productImageSrc,
								productUrl: getProductUrl(notificationRequiredInfo.productUrl),
						  }
						: null
				}
				defaultClose={() => closeModal()}
			/>
		),
		closeOnOutClick: true,
		modalBoxClassName: 'w-full max-w-4xl',
	});

	const { user } = useUser();
	const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

	const [email, setEmail] = useState<string>('');
	const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

	const handleFavouriteClick = async (e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();

		if (!user?.username) {
			setIsEmailModalOpen(true);
			return;
		}

		try {
			if (isFavourite) {
				setShowConfirmationDialog(true);
			} else {
				const {
					haggleStartDate,
					haggleEventId,
					productId,
					productName,
					productUrl,
				} = notificationRequiredInfo || {};

				await haggleApiService.createNotification(
					user.username,
					'haggle_favourite_reminder',
					false,
					haggleStartDate || '',
					haggleStartDate || '',
					haggleEventId || 0,
					String(productId || ''),
					productName || '',
					getProductUrl(productUrl || ''),
					productImageSrc,
					true,
					productHaggle?.user.username,
				);

				toast.success('Added to favourites!');
				refetchFavourites?.();
			}
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : 'Unknown error';
			console.error(message);
			toast.error('Failed to process your favourite. Please try again.');
		}
	};

	const handleConfirmRemove = async () => {
		try {
			const {
				haggleStartDate,
				haggleEventId,
				productId,
				productName,
				productUrl,
			} = notificationRequiredInfo || {};

			await haggleApiService.createNotification(
				user?.username || '',
				'haggle_favourite_reminder',
				true,
				haggleStartDate || '',
				haggleStartDate || '',
				haggleEventId || 0,
				String(productId || ''),
				productName || '',
				getProductUrl(productUrl || ''),
				productImageSrc,
				true,
				productHaggle?.user.username,
			);

			toast.success('Removed from favourites!');
			setShowConfirmationDialog(false);
			refetchFavourites?.();
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : 'Unknown error';
			console.error(message);
			toast.error('Failed to remove from favourites. Please try again.');
		}
	};

	const handleCancelRemove = () => {
		setShowConfirmationDialog(false);
	};

	const handleEmailSubmit = async () => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (emailRegex.test(email)) {
			try {
				const {
					haggleStartDate,
					haggleEventId,
					productId,
					productName,
					productUrl,
				} = notificationRequiredInfo || {};

				await haggleApiService.createNotification(
					email,
					'haggle_favourite_reminder',
					false,
					haggleStartDate || '',
					haggleStartDate || '',
					haggleEventId || 0,
					String(productId || ''),
					productName || '',
					getProductUrl(productUrl || ''),
					productImageSrc,
					true,
					productHaggle?.user.username,
				);

				toast.success('Added to favourites!');
				setIsEmailModalOpen(false);
			} catch (error: any) {
				console.error(error.message);
				toast.error('Failed to process your favourite. Please try again.');
			}
		} else {
			toast.warning('Please provide a valid email address.');
		}
	};

	// Helper to render the start date and time for the top-left badge
	const renderCountdown = () => {
		if (!productHaggle || new Date(productHaggle.startDate) <= new Date()) {
			return null;
		}

		return formatDateTimeShort(productHaggle.startDate);
	};

	// Determine if the haggle is upcoming
	const isUpcoming =
		productHaggle && new Date(productHaggle.startDate) > new Date();
	const shadowClass = isUpcoming
		? 'glassy-shadow-inset-inward'
		: 'glassy-shadow-inset-outward';

	return (
		<div className="relative h-full flex flex-col ">
			{/* Confirmation Dialog */}
			{showConfirmationDialog && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
					<div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
						<h3 className="text-lg font-medium text-gray-900 mb-4">
							Remove from Favourites
						</h3>
						<p className="text-sm text-gray-500 mb-6">
							Are you sure you want to remove this item from your favourites?
						</p>
						<div className="flex justify-end space-x-3">
							<button
								onClick={handleCancelRemove}
								className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
							>
								Cancel
							</button>
							<button
								onClick={handleConfirmRemove}
								className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
							>
								Remove
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Email Modal */}
			{isEmailModalOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
					<div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
						<h3 className="text-lg font-medium text-gray-900 mb-4">
							Get Notified
						</h3>
						<p className="text-sm text-gray-500 mb-4">
							Enter your email to get notified when this product becomes
							available.
						</p>
						<input
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="Enter your email"
							className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purpleBrand-500 focus:border-transparent"
						/>
						<div className="flex justify-end space-x-3 mt-6">
							<button
								onClick={() => setIsEmailModalOpen(false)}
								className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
							>
								Cancel
							</button>
							<button
								onClick={handleEmailSubmit}
								className="px-4 py-2 text-sm font-medium text-white bg-purpleBrand-500 rounded-md hover:bg-purpleBrand-600 transition-colors"
							>
								Notify Me
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Modal for notifications */}
			{modal}

			<Link
				to={
					productHaggle && !productHaggle.hostHaggleEventOnShopify
						? detailsPageUrl(productHaggle?.isCSVHaggle || false)
						: productHref || '#'
				}
				state={
					productHaggle && !productHaggle.hostHaggleEventOnShopify
						? { vendorEmail: productHaggle?.user.username }
						: undefined
				}
				target={
					productHaggle && productHaggle.hostHaggleEventOnShopify
						? '_blank'
						: undefined
				}
				rel={
					productHaggle && productHaggle.hostHaggleEventOnShopify
						? 'noopener noreferrer'
						: undefined
				}
				className="block h-full"
				data-test="product-card"
			>
				{/* --- New Figma Card Structure --- */}
				<div
					className={`group relative bg-gray-50 backdrop-blur-md rounded-[18px] border border-white/50 transition-all duration-300 ease-in-out overflow-hidden h-full flex flex-col p-3 ${shadowClass}`}
				>
					{/* Top Section: Image and Countdown/Lock */}
					<div className="relative flex-shrink-0 mb-4">
						{/* Glassy Overlay for Image/Countdown Area */}
						<div className={`rounded-xl bg-white`}>
							{/* Countdown Timer Badge (Top-Left) */}
							{productHaggle &&
								new Date(productHaggle.startDate) > new Date() && (
									<div className="absolute top-2 left-1 right-1 md:right-2 z-10">
										<div className="flex items-center justify-center text-[0.7rem] gap-1 px-2 md:px-3 py-1 bg-white/70 backdrop-blur-sm rounded-full font-light text-gray-600 whitespace-nowrap">
											<FaLock className="w-3 h-3 flex-shrink-0" />
											<span className="truncate">
												{renderCountdown() || '...'}
											</span>
										</div>
									</div>
								)}

							{/* Product Image */}
							<div
								className={`aspect-square w-full rounded-lg overflow-hidden  ${shadowClass} `}
							>
								<img
									alt={productImageAlt}
									src={
										productImageSrc ||
										'data:image/svg+xml;charset=UTF-8,' +
											encodeURIComponent(`
                                            <svg xmlns="http://www.w3.org/2000/svg" width="216" height="144" viewBox="0 0 216 144">
                                            <rect width="100%" height="100%" fill="#e5e7eb" />
                                            <text x="50%" y="50%" fill="#6b7280" font-size="14" font-family="Arial, Helvetica, sans-serif"
                                                    text-anchor="middle" dominant-baseline="middle">
                                                No Image
                                            </text>
                                            </svg>
                                        `)
									}
									className={`object-contain object-center w-full h-full group-hover:scale-105 transition-transform duration-300 ease-in-out ${shadowClass}`}
								/>
							</div>
						</div>
					</div>

					{/* Bottom Section: Info and Icons */}
					<div className="px-1 flex-1 flex flex-col justify-between">
						{/* Product Info */}

						{/* Price and Action Icons - side by side */}
						<div className="flex items-end justify-between mt-auto pt-2">
							{/* Price */}
							<span className="text-md text-gray-900">
								<div>
									{/* Brand */}
									<p className="text-gray-600 mb-0.5 line-clamp-1">
										{productVendor || 'Apple'}
									</p>

									{/* Product Name */}
									<h3 className="line-clamp-2 font-medium text-gray-900 mb-2 line-clamp-2 hover:text-purpleBrand-500 transition-colors duration-200">
										{productName}
									</h3>
								</div>
								<div className="text-gray-900">{formatPrice(productPrice)}</div>
							</span>

							{/* Action Icons */}
							<div className="flex flex-col gap-2">
								{/* Heart Icon */}
								<button
									onClick={handleFavouriteClick}
									className="p-1.5 bg-white/70 backdrop-blur-sm rounded-full transition-all"
									aria-label={
										isFavourite ? 'Remove from favourites' : 'Add to favourites'
									}
								>
									{isFavourite ? (
										<BsHeartFill className="text-red-500 w-5 h-5" />
									) : (
										<BsHeart className="text-gray-600 w-5 h-5" />
									)}
								</button>

								{/* Bell Icon - only show for home card type with notification info */}
								{cardType === ProductCardType.Home &&
									notificationRequiredInfo &&
									notificationRequiredInfo.haggleStartDate &&
									new Date(notificationRequiredInfo.haggleStartDate) >
										new Date() && (
										<button
											onClick={(e) => {
												e.stopPropagation();
												e.preventDefault(); // Prevents link navigation
												openModal();
											}}
											className="p-1.5 bg-white/70 backdrop-blur-sm rounded-full transition-all"
											aria-label="Get notified when available"
										>
											<FaBell className="text-gray-600 w-5 h-5" />
										</button>
									)}
							</div>
						</div>
					</div>
				</div>
			</Link>
		</div>
	);
};

export default ProductCard;
